import React, { useContext, useEffect } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import '../../../css/ExpandableRubricScoringTable.scss';

import HtmlComponent from '../HtmlComponent';

import { register } from '../../../i18n';

import scoringService from '../../services/ScoringService';
import utilsService from '../../services/UtilsService';
import { isRubricCategoryFullyChecked, isRubricCheckboxScoring } from '../../utils/rubricCheckboxScoring';

const t = register('TeacherAIScoreResultsPanel');
const t2 = register('AriaLabels');
const tPanel = register('PanelMenu');

/**
 * **ExpandableRubricScoringTable:** Displays rubric scoring data in collapsed or expanded mode
 *
 * **Collapsed mode:** Category | Score | Justification (optional)
 * **Expanded mode:** Category | [All Rubric Columns] | Score | Max Points (optional) | Justification (optional)
 *
 * @param {Object} props - Component props
 * @param {string} props.lessonElementId - The lesson element ID
 * @param {Array<Object>} [props.aiRubricCriteria=null] - AI rubric criteria data (optional, for AI Feedback panel)
 * @param {boolean} [props.expanded=false] - Whether to show expanded view with all rubric columns
 * @param {boolean} [props.showJustificationWhenCollapsed=false] - Whether to show justification column when collapsed
 * @param {boolean} [props.showMaxPoints=false] - Whether to show max points column in expanded mode
 * @param {boolean} [props.editable=false] - Whether to show input fields for scoring (for TeacherScorePanel)
 * @param {Function} [props.onScoreChange] - Callback when score changes (optional)
 * @returns {React.Component|null} The rubric scoring table component or null if no rubric data
 */
const ExpandableRubricScoringTable = observer(({
  lessonElementId,
  aiRubricCriteria = null,
  expanded = false,
  showJustificationWhenCollapsed = false,
  showMaxPoints = false,
  editable = false,
  onScoreChange
} = {}) => {
  const { scoringManager, responseManager } = useContext(MobXProviderContext);

  const rubric = scoringManager.rubricMap.get(lessonElementId);
  const checkboxMode = Boolean(rubric && isRubricCheckboxScoring(rubric));

  useEffect(() => {
    const state = responseManager.getLessonElementState(lessonElementId);
    if (state && state.subscore) {
      scoringManager.setSubscore(
        lessonElementId,
        JSON.parse(state.subscore),
        state.isScored
      );
    }
  }, [lessonElementId, responseManager, scoringManager]);

  if (!rubric) {
    return null;
  }

  // Handle input change for editable mode
  const handleChange = (event, categoryId) => {
    const str = event.target.value;
    if (utilsService.ensureNumericInput(str)) {
      scoringService.setCategoryTempScoreFromTable(lessonElementId, categoryId, str);

      if (onScoreChange) {
        onScoreChange();
      }
    }
  };

  // Handle input blur for editable mode
  const handleBlur = (event, categoryId, maxPoints) => {
    let str = event.target.value;

    if (utilsService.isNullOrEmpty(str) || str.trim() === '.') {
      scoringService.setCategoryTempScoreFromTable(lessonElementId, categoryId, '0.00');
      return;
    }
    if (utilsService.ensureNumericInput(str)) {
      const num = parseFloat(str);
      const maxScore = parseFloat(maxPoints);
      if (num > maxScore) {
        str = maxScore.toFixed(2);
      }
      if (num < 0) {
        str = '0.00';
      }
      scoringService.setCategoryTempScoreFromTable(lessonElementId, categoryId, str);
    }
  };

  const fieldValue = (category) => {
    if (typeof category.calculatedTempScore !== 'undefined') {
      return category.calculatedTempScore;
    } else {
      return '';
    }
  };

  // Collapsed mode - Category | Score | Justification (optional)
  if (!expanded) {
    return (
      <table aria-label={t('rubricCriteriaTable')} className='expandable-rubric-scoring-table collapsed'>
        <thead>
          <tr>
            <th aria-label={t('category')}>{t('category')}</th>
            <th aria-label={checkboxMode ? tPanel('rubricCriterionMet') : t('score')}>
              {checkboxMode ? tPanel('rubricCriterionMet') : t('score')}
            </th>
            {showJustificationWhenCollapsed && aiRubricCriteria && (
              <th aria-label={t('overallJustification')}>{t('overallJustification')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {rubric.categories.map((category, index) => {
            // Find matching AI criteria for this category
            const aiCriteria = aiRubricCriteria?.find(
              (criteria) => criteria.categoryId === category.categoryId
            );

            return (
              <tr key={`rubric_collapsed_${index}`}>
                <td className='category-name'>
                  <HtmlComponent htmlStr={category.name} />
                </td>
                <td className='category-score'>
                  {checkboxMode ? (
                    <>
                      {editable ? (
                        <input
                          aria-label={t2('rubricCategoryMetCheckbox')}
                          checked={isRubricCategoryFullyChecked(category)}
                          onChange={(e) => {
                            scoringService.setCategoryCheckboxScore(
                              lessonElementId,
                              category.categoryId,
                              e.target.checked
                            );
                            if (onScoreChange) {
                              onScoreChange();
                            }
                          }}
                          type='checkbox'
                        />
                      ) : (
                        <input
                          aria-label={t2('rubricCategoryMetCheckbox')}
                          checked={isRubricCategoryFullyChecked(category)}
                          disabled
                          type='checkbox'
                        />
                      )}
                    </>
                  ) : (
                    editable ? (
                      <>
                        <input
                          aria-label={t2('categoryfinalscore')}
                          onBlur={(e) => handleBlur(e, category.categoryId, category.maxPoints)}
                          onChange={(e) => handleChange(e, category.categoryId)}
                          type='text'
                          value={fieldValue(category)}
                        />
                        <span>/</span><span>{category.maxPoints}</span>
                      </>
                    ) : (
                      <>
                        <span>{category.calculatedTempScore || '0.00'}</span>
                        <span>/</span><span>{category.maxPoints}</span>
                      </>
                    )
                  )}
                </td>
                {showJustificationWhenCollapsed && aiRubricCriteria && (
                  <td className='category-justification'>
                    {aiCriteria?.justification || ''}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  // Expanded mode - full rubric: Category | [All Rubric Columns] | Score | Max Points (optional) | Justification
  return (
    <table aria-label={t('rubricCriteriaTable')} className='expandable-rubric-scoring-table expanded'>
      <thead>
        <tr>
          <th className='category-header'>{t('category')}</th>
          {rubric.columns.map((column, colIndex) => (
            <th key={`rubric_header_${colIndex}`} className='rubric-column-header'>
              <HtmlComponent htmlStr={column} />
            </th>
          ))}
          <th className='score-header'>{checkboxMode ? tPanel('rubricCriterionMet') : t('score')}</th>
          {showMaxPoints && !checkboxMode && (
            <th className='max-points-header'>{t('maxPoints')}</th>
          )}
          {aiRubricCriteria && (
            <th className='justification-header'>{t('overallJustification')}</th>
          )}
        </tr>
      </thead>
      <tbody>
        {rubric.categories.map((category, catIndex) => {
          // Find matching AI criteria for this category
          const aiCriteria = aiRubricCriteria?.find(
            (criteria) => criteria.categoryId === category.categoryId
          );

          return (
            <tr key={`rubric_expanded_${catIndex}`}>
              <td className='category-name'>
                <HtmlComponent htmlStr={category.name} />
              </td>
              {category.columns.map((column, colIndex) => {
                const isSelected = scoringManager.isSelectedCell(
                  category.categoryId,
                  colIndex,
                  lessonElementId
                );

                return (
                  <td
                    key={`rubric_cell_${catIndex}_${colIndex}`}
                    className={classNames({
                      'rubric-cell': true,
                      'selected': isSelected
                    })}
                  >
                    <HtmlComponent htmlStr={column} />
                  </td>
                );
              })}
              <td className='category-score'>
                {checkboxMode ? (
                  <>
                    {editable ? (
                      <input
                        aria-label={t2('rubricCategoryMetCheckbox')}
                        checked={isRubricCategoryFullyChecked(category)}
                        onChange={(e) => {
                          scoringService.setCategoryCheckboxScore(
                            lessonElementId,
                            category.categoryId,
                            e.target.checked
                          );
                          if (onScoreChange) {
                            onScoreChange();
                          }
                        }}
                        type='checkbox'
                      />
                    ) : (
                      <input
                        aria-label={t2('rubricCategoryMetCheckbox')}
                        checked={isRubricCategoryFullyChecked(category)}
                        disabled
                        type='checkbox'
                      />
                    )}
                  </>
                ) : (
                  editable ? (
                    <>
                      <input
                        aria-label={t2('categoryfinalscore')}
                        onBlur={(e) => handleBlur(e, category.categoryId, category.maxPoints)}
                        onChange={(e) => handleChange(e, category.categoryId)}
                        type='text'
                        value={fieldValue(category)}
                      />
                      <span>/</span><span>{category.maxPoints}</span>
                    </>
                  ) : (
                    <>
                      <span>{category.calculatedTempScore || '0.00'}</span>
                      <span>/</span><span>{category.maxPoints}</span>
                    </>
                  )
                )}
              </td>
              {showMaxPoints && !checkboxMode && (
                <td className='max-points'>{category.maxPoints}</td>
              )}
              {aiRubricCriteria && (
                <td className='category-justification'>
                  {aiCriteria?.justification || ''}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
});

export default ExpandableRubricScoringTable;
